/**
 * Incoming-CC mapping definitions — the editor twin of the firmware's
 * `app/lib/midulate/cc_mapping.py` (MDL-263 epic).
 *
 * Both implementations must pass the same vector table,
 * `packages/shared/test/fixtures/cc_scaling_vectors.json`
 * (firmware twin: `midulate/tests/unit/fixtures/cc_scaling_vectors.json`).
 * Change them only in lockstep.
 *
 * Spec: https://29ways.atlassian.net/wiki/spaces/M2D2/pages/130940930
 */

import { EFFECT_TITLES } from './effects.js';
import { NOTE_VALUE_DIVISIONS } from './rateDivisions.js';
import { ACCENT_EVERY_OFFERED } from './accentEvery.js';
import { PHASE_OFFSET_RANGE } from './phaseOffset.js';

function range(from, to) {
  const out = [];
  for (let i = from; i <= to; i++) out.push(i);
  return out;
}

/**
 * Incoming CCs that can never be mapped.
 *
 * Rule (locked 17 July 2026): hard-reserve only what MIDI 1.0 itself defines
 * (0/32 Bank Select, 120–127 Channel Mode) plus one contiguous proprietary
 * block at 102–110 (102 global bypass, 103 rate multiplier ROADMAP,
 * 104–110 headroom). CCs 1–31 and 111–119 stay mappable. Do NOT expand this
 * set after ship — it silently breaks saved patches.
 */
export const RESERVED_CCS = new Set([0, 32, ...range(102, 110), ...range(120, 127)]);

export const RATE_HZ_MIN = 0.1;
export const RATE_HZ_MAX = 10.0;

/** Exact order of the accent knob (Effect.vue clock-face order). */
export const ACCENT_OPTIONS = [
  'none',
  'small_positive',
  'large_positive',
  'mask',
  'large_negative',
  'small_negative',
];

/**
 * Exact order of the Every knob (MDL-418): Bar, then the offered counts. 1 is
 * not offered by the knob, so a CC never lands on it. Single source:
 * ACCENT_EVERY_OFFERED — mirrors the firmware's cc_mapping.ACCENT_EVERY_OPTIONS.
 */
export const ACCENT_EVERY_CC_OPTIONS = ['bar', ...ACCENT_EVERY_OFFERED];

/** Exact order of the wander boundary-mode control. */
export const BOUNDARY_MODE_OPTIONS = ['hard', 'soft'];

/**
 * The offered synced-rate divisions, ascending, exactly as the Div knob renders
 * them (MDL-288): 5 short note values + 6 bar multiples. A mapped incoming CC
 * buckets across these. Single source: the offered NOTE_VALUE_DIVISIONS order —
 * mirrors the firmware's effect.RATE_DIVISION_ORDER.
 */
export const RATE_DIVISION_OPTIONS = NOTE_VALUE_DIVISIONS.map(o => o.value);

const lin = (lo, hi) => ({ kind: 'linear', lo, hi });
const RATE = { kind: 'rate' };
const ACCENT = { kind: 'enum', options: ACCENT_OPTIONS };
// `labels` is editor-only (the firmware has no use for it). The Every counts
// are plain numbers, so they need their own labels: ENUM_VALUE_LABELS keys
// '2', '4', '8', '12' belong to the Div knob and read "2 Bars" etc.
const ACCENT_EVERY = {
  kind: 'enum',
  options: ACCENT_EVERY_CC_OPTIONS,
  labels: { bar: 'Bar', ...Object.fromEntries(ACCENT_EVERY_OFFERED.map(n => [n, String(n)])) },
};
// On/off switch target (non-parameter, no output range) — applied as a
// runtime toggle with level semantics (0 = off, non-zero = on).
const SWITCH = { kind: 'switch' };
const MIN_MAX = { min: lin(0, 127), max: lin(0, 127) };
// Phase Offset (MDL-301): degrees, so a plain linear target like Min and Max
// (takes lo/hi). Mirrors the firmware's _PHASE_OFFSET.
const PHASE_OFFSET = lin(-PHASE_OFFSET_RANGE, PHASE_OFFSET_RANGE);

/**
 * Per-effect-type whitelist of mappable parameter names, with bounds and
 * kind. Mirrors the firmware module exactly:
 *   linear — lo/hi int bounds (mapping ranges clamp to them)
 *   rate   — log Hz (RATE_HZ_MIN..MAX) when clock_sync is false;
 *            the RATE_DIVISION_OPTIONS enum (no range) when true
 *   enum   — options in exact knob order; takes no lo/hi
 *
 * Not mappable by design: channel, cc (output routing), clock_sync,
 * accent_beat (inert once accent_every is set), type.
 */
export const MAPPABLE_PARAMS = {
  bounce: { ...MIN_MAX, rate: RATE, accent: ACCENT, accent_every: ACCENT_EVERY },
  flipflop: {
    rate: RATE,
    accent: ACCENT,
    accent_every: ACCENT_EVERY,
    flip: lin(0, 127),
    flop: lin(0, 127),
  },
  glitch: {
    ...MIN_MAX,
    rate: RATE,
    focus: lin(0, 100),
    spread: lin(-100, 100),
    bias: lin(-99, 99),
    beat_affinity: lin(-100, 100),
  },
  swell: {
    ...MIN_MAX,
    rate: RATE,
    accent: ACCENT,
    accent_every: ACCENT_EVERY,
    gradient: lin(-100, 100),
    balance: lin(-100, 100),
    skew: lin(-100, 100),
  },
  wander: {
    ...MIN_MAX,
    rate: RATE,
    step_size: lin(1, 127),
    boundary_mode: { kind: 'enum', options: BOUNDARY_MODE_OPTIONS },
  },
  zigzag: {
    ...MIN_MAX,
    rate: RATE,
    accent: ACCENT,
    accent_every: ACCENT_EVERY,
    skew: lin(-100, 100),
  },
};

// Phase is on every effect except wander, which has no phase to offset.
for (const type of ['bounce', 'flipflop', 'glitch', 'swell', 'zigzag']) {
  MAPPABLE_PARAMS[type].phase_offset = PHASE_OFFSET;
}

// `mute` is a universal per-effect switch target on every effect type — an
// incoming CC sets the effect's active state (engage convention, like the
// global bypass CC): 0 = silenced, 1+ = active. Mirrors the firmware
// cc_mapping.py. Applied against mute state, not a knob, so it takes no range
// and has no ghost marker.
for (const params of Object.values(MAPPABLE_PARAMS)) {
  params.mute = SWITCH;
}

/**
 * Scale an incoming CC value (raw, 0–127) onto a parameter.
 *
 * spec is a MAPPABLE_PARAMS entry; lo/hi are the mapping's output range
 * (ignored for enum-like params). lo > hi is inversion.
 */
export function scaleCcValue(spec, raw, lo, hi, clockSynced = false) {
  const { kind } = spec;
  if (kind === 'switch') return raw >= 1 ? 1 : 0;
  if (kind === 'enum' || (kind === 'rate' && clockSynced)) {
    const options = kind === 'enum' ? spec.options : RATE_DIVISION_OPTIONS;
    const n = options.length;
    return options[Math.min(n - 1, Math.floor((raw * n) / 128))];
  }
  if (kind === 'rate') {
    // Log-space interpolation keeps the log knob's feel over any sub-range;
    // both bounds are positive so inversion works too. 2 dp like firmware.
    return Math.round(lo * (hi / lo) ** (raw / 127) * 100) / 100;
  }
  return Math.round(lo + ((hi - lo) * raw) / 127);
}

/**
 * Full output range for a mapping without lo/hi, or [null, null] for
 * enum-like params (which take no range).
 */
export function defaultRange(spec, clockSynced = false) {
  const { kind } = spec;
  if (kind === 'enum' || kind === 'switch' || (kind === 'rate' && clockSynced)) return [null, null];
  if (kind === 'rate') return [RATE_HZ_MIN, RATE_HZ_MAX];
  return [spec.lo, spec.hi];
}

// ---------------------------------------------------------------------------
// Editor helpers (badges / assignment popover)
// ---------------------------------------------------------------------------

/** Short display labels matching the knob labels the forms render. */
export const PARAM_LABELS = {
  min: 'Min',
  max: 'Max',
  rate: 'Rate',
  accent: 'Accent',
  accent_every: 'Every',
  phase_offset: 'Phase',
  flip: 'Flip',
  flop: 'Flop',
  focus: 'Focus',
  spread: 'Spread',
  bias: 'Bias',
  beat_affinity: 'Beat Affinity',
  gradient: 'Grad',
  balance: 'Bal',
  skew: 'Skew',
  step_size: 'Step',
  boundary_mode: 'Boundary',
  mute: 'Mute',
};

/**
 * Display labels for enum option values, matching the labels the editor
 * knobs render (accent clock-face, wander boundary, rate division knob).
 */
export const ENUM_VALUE_LABELS = {
  none: 'None',
  small_positive: 'Small +',
  large_positive: 'Large +',
  mask: 'Mask',
  large_negative: 'Large −',
  small_negative: 'Small −',
  hard: 'Hard',
  soft: 'Soft',
  // Note-value Div (MDL-288). The knob readout is a glyph, but the incoming-CC
  // range overlay is text, so these are concise British names.
  '1/4b': 'Semiquaver',
  '1/3b': 'Quaver triplet',
  '1/2b': 'Quaver',
  '2/3b': 'Crotchet triplet',
  '1b': 'Crotchet',
  '2b': 'Minim',
  '1': '1 Bar',
  '2': '2 Bars',
  '4': '4 Bars',
  '8': '8 Bars',
  '12': '12 Bars',
  '16': '16 Bars',
};

/**
 * The incoming-CC value range that selects each option of an enum-like
 * param, derived from the bucket rule `index = min(N−1, (v×N)//128)` —
 * the same maths the firmware applies, so the displayed map is exact.
 *
 * Returns [{ option, label, from, to }] in option order, or null when the
 * param is not enum-like (continuous params use lo/hi ranges instead).
 */
export function enumValueRanges(effect, param) {
  const spec = paramSpec(effect, param);
  if (!spec) return null;
  let options = null;
  if (spec.kind === 'enum') options = spec.options;
  else if (spec.kind === 'rate' && effect.clock_sync === true) options = RATE_DIVISION_OPTIONS;
  if (!options) return null;
  const n = options.length;
  return options.map((option, i) => ({
    option,
    label: spec.labels?.[option] ?? ENUM_VALUE_LABELS[option] ?? String(option),
    from: Math.ceil((128 * i) / n),
    to: Math.ceil((128 * (i + 1)) / n) - 1,
  }));
}

/**
 * Presence-gated capability check: firmware without incoming-CC mapping
 * omits the key entirely (baseline/diff model).
 */
export function isCcMappingSupported(capabilities) {
  return capabilities?.capabilities?.cc_mapping != null;
}

/**
 * Whether the device supports device-side MIDI Learn (the `learn` sub-flag of
 * cc_mapping — MDL-314). Gates the Learn button; older firmware omits it.
 */
export function isMidiLearnSupported(capabilities) {
  return capabilities?.capabilities?.cc_mapping?.learn === true;
}

/** The MAPPABLE_PARAMS spec for a param on this effect, or null. */
export function paramSpec(effect, param) {
  return MAPPABLE_PARAMS[effect?.type]?.[param] ?? null;
}

/**
 * Whether a mapping on this param takes lo/hi range fields. Enums, switches
 * (mute), and clock-synced rate (an enum of divisions) take no range.
 */
export function isRanged(effect, param) {
  const spec = paramSpec(effect, param);
  if (!spec) return false;
  if (spec.kind === 'enum' || spec.kind === 'switch') return false;
  if (spec.kind === 'rate' && effect.clock_sync === true) return false;
  return true;
}

/**
 * Normalised view of an effect's mapping for a param: { cc, lo, hi } with
 * lo/hi filled from the param's full range (bare-int back-compat included),
 * or null when unmapped.
 */
export function getMapping(effect, param) {
  const entry = effect?.mappings?.[param];
  if (entry == null) return null;
  const spec = paramSpec(effect, param);
  const [dlo, dhi] = spec ? defaultRange(spec, effect.clock_sync === true) : [null, null];
  if (typeof entry === 'number') return { cc: entry, lo: dlo, hi: dhi };
  return { cc: entry.cc, lo: entry.lo ?? dlo, hi: entry.hi ?? dhi };
}

/**
 * Write a mapping in object form, mutating the effect in place (the same
 * pattern as every knob — the parent deep-watch persists it). lo/hi equal
 * to the param's full range are omitted so configs stay minimal; enum-like
 * params never carry them.
 */
export function setMapping(effect, param, { cc, lo, hi }) {
  const entry = { cc };
  if (isRanged(effect, param)) {
    const [dlo, dhi] = defaultRange(paramSpec(effect, param), effect.clock_sync === true);
    if (lo != null && lo !== dlo) entry.lo = lo;
    if (hi != null && hi !== dhi) entry.hi = hi;
  }
  if (!effect.mappings) effect.mappings = {};
  effect.mappings[param] = entry;
}

/** Remove a mapping in place, pruning an emptied mappings dict entirely. */
export function clearMapping(effect, param) {
  if (!effect.mappings) return;
  delete effect.mappings[param];
  if (Object.keys(effect.mappings).length === 0) delete effect.mappings;
}

/**
 * Find every current owner of a CC anywhere in the patch. Returns an array of
 * { effectIndex, param, label } (label like "Swell 2 · Grad") in patch order,
 * empty when the CC is unused. skipEffectIndex/skipParam exclude the mapping
 * being edited so re-assigning the same CC to the same target isn't listed.
 *
 * A CC may drive several params, on one or many effects — the firmware fully
 * supports this (spec §3/§8). The assignment modal uses this to surface a
 * non-blocking "also controls …" warning naming all other targets, not a
 * rejection.
 */
export function findCcOwners(effects, cc, { skipEffectIndex = -1, skipParam = null } = {}) {
  const owners = [];
  if (!Array.isArray(effects)) return owners;
  for (let i = 0; i < effects.length; i++) {
    const mappings = effects[i]?.mappings;
    if (!mappings) continue;
    for (const [param, entry] of Object.entries(mappings)) {
      if (i === skipEffectIndex && param === skipParam) continue;
      const entryCc = typeof entry === 'number' ? entry : entry?.cc;
      if (entryCc === cc) {
        owners.push({ effectIndex: i, param, label: ccOwnerLabel(effects, i, param) });
      }
    }
  }
  return owners;
}

/**
 * First owner of a CC, or null — a thin wrapper over findCcOwners for callers
 * that only need existence or the primary owner.
 */
export function findCcOwner(effects, cc, opts = {}) {
  return findCcOwners(effects, cc, opts)[0] ?? null;
}

function ccOwnerLabel(effects, index, param) {
  const type = effects[index]?.type;
  const title = EFFECT_TITLES[type] ?? type ?? 'Effect';
  const sameType = effects.filter((e) => e?.type === type).length;
  const ordinal = sameType > 1
    ? ` ${effects.slice(0, index + 1).filter((e) => e?.type === type).length}`
    : '';
  return `${title}${ordinal} · ${PARAM_LABELS[param] ?? param}`;
}
